import { windowSpanDays, type DateWindow } from "@nexus/contracts";
import { parseYmdLocal } from "./ymd";

const SHORT_SPAN_MAX_DAYS = 7;
const MEDIUM_SPAN_MAX_DAYS = 31;

export interface PeriodBucket {
  key: string;
  label: string;
  dayOffsetStart: number;
  dayOffsetEnd: number;
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function bucketLabel(date: Date): string {
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yy = String(date.getFullYear()).slice(2);
  return `${dd}/${mm}/${yy}`;
}

function bucketCountForSpan(spanDays: number): number {
  if (spanDays <= SHORT_SPAN_MAX_DAYS) return spanDays;
  if (spanDays <= MEDIUM_SPAN_MAX_DAYS) return 4;
  return 12;
}

function periodStartFromWindow(window: DateWindow): Date {
  return parseYmdLocal(window.startDate) ?? new Date(`${window.startDate}T00:00:00`);
}

/**
 * Buckets for the resolved Dashboard window. Origin is `startDate`.
 * Span ≤7 → one bucket per day; ≤31 → 4; otherwise 12.
 */
export function computePeriodBuckets(window: DateWindow): PeriodBucket[] {
  const totalDays = windowSpanDays(window);
  const bucketCount = bucketCountForSpan(totalDays);
  const periodStart = periodStartFromWindow(window);

  const buckets: PeriodBucket[] = [];
  for (let i = 0; i < bucketCount; i++) {
    const dayOffsetStart = Math.floor((i * totalDays) / bucketCount);
    const dayOffsetEnd = Math.floor(((i + 1) * totalDays) / bucketCount) - 1;
    const representative = addDays(periodStart, dayOffsetStart);
    buckets.push({
      key: `bucket-${i}`,
      label: bucketLabel(representative),
      dayOffsetStart,
      dayOffsetEnd,
    });
  }
  return buckets;
}

export function computeChartTitle(window: DateWindow): string {
  const totalDays = windowSpanDays(window);
  const bucketCount = bucketCountForSpan(totalDays);
  const periodStart = periodStartFromWindow(window);
  const periodEnd = parseYmdLocal(window.endDate) ?? addDays(periodStart, totalDays - 1);

  if (totalDays <= MEDIUM_SPAN_MAX_DAYS) {
    const startStr = `${periodStart.toLocaleDateString("en-US", { month: "short" })} ${periodStart.getDate()}`;
    const endStr = `${periodEnd.toLocaleDateString("en-US", { month: "short" })} ${periodEnd.getDate()}, ${periodEnd.getFullYear()}`;
    return `${startStr} – ${endStr}`;
  }

  const lastBucketStart = Math.floor(((bucketCount - 1) * totalDays) / bucketCount);
  const lastRep = addDays(periodStart, lastBucketStart);

  if (totalDays <= 90) {
    const firstMonth = periodStart.toLocaleDateString("en-US", { month: "long" });
    const lastMonthYear = `${lastRep.toLocaleDateString("en-US", { month: "long" })} ${lastRep.getFullYear()}`;
    return `${firstMonth} – ${lastMonthYear}`;
  }

  const firstMonthYear = `${periodStart.toLocaleDateString("en-US", { month: "long" })} ${periodStart.getFullYear()}`;
  const lastMonthYear = `${lastRep.toLocaleDateString("en-US", { month: "long" })} ${lastRep.getFullYear()}`;
  return `${firstMonthYear} – ${lastMonthYear}`;
}

export function bucketIndexForOffset(offset: number, buckets: PeriodBucket[]): number {
  const bucketCount = buckets.length;
  const totalDays = buckets[bucketCount - 1].dayOffsetEnd + 1;
  return Math.min(Math.floor((offset * bucketCount) / totalDays), bucketCount - 1);
}
